import type React from 'react';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import { useGetGroupsCount, useGetGroupsInfinite } from '@vlabs/api/artemy/client';
import type { GroupsList } from '@vlabs/api/artemy/model';
import { useIntersectionObserver } from '@vlabs/shared/hooks';
import Loader from '@vlabs/shared/legacy-components/loader/Loader';

import { useSourceGroupListViewerTranslation } from './i18n';
import { SourceGroupCard } from './source-group-card/SourceGroupCard';
import st from './SourceGroupListViewer.module.sass';

const GROUPS_PAGE_SIZE = 10;

const getNextPageParam = (lastPage: GroupsList, allPages: GroupsList[]) => (
  (lastPage.groups?.length ?? 0) < GROUPS_PAGE_SIZE
    ? undefined
    : allPages.length + 1
);

function SourceGroupListViewer(): React.JSX.Element {
  const { t } = useSourceGroupListViewerTranslation();

  const getGroups = useGetGroupsInfinite(
    { page_size: GROUPS_PAGE_SIZE },
    {
      query: {
        getNextPageParam,
        initialPageParam: 1,
      },
    },
  );
  const getGroupsCount = useGetGroupsCount();

  const groups = useMemo(
    () => getGroups.data?.pages.flatMap((page) => page.groups ?? []) ?? [],
    [getGroups.data],
  );

  // Свёрнутость групп: undefined в мапе = развёрнута; по умолчанию развёрнута только первая
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current || groups.length === 0) return;
    initializedRef.current = true;

    const initialCollapsed = groups.slice(1).reduce<Record<string, boolean>>((acc, group) => {
      acc[group.group_id] = true;
      return acc;
    }, {});
    setCollapsedGroups(initialCollapsed);
  }, [groups]);

  const [areAllExpanded, setAreAllExpanded] = useState(false);

  const toggleExpandAll = useCallback(() => {
    if (areAllExpanded) {
      const allCollapsed = groups.reduce<Record<string, boolean>>((acc, group) => {
        acc[group.group_id] = true;
        return acc;
      }, {});
      setCollapsedGroups(allCollapsed);
      setAreAllExpanded(false);
      return;
    }

    setCollapsedGroups({});
    setAreAllExpanded(true);
  }, [areAllExpanded, groups]);

  const handleScroll = useCallback(() => {
    if (getGroups.hasNextPage && !getGroups.isFetchingNextPage) {
      getGroups.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getGroups.fetchNextPage, getGroups.hasNextPage, getGroups.isFetchingNextPage]);

  const sentinelRef = useIntersectionObserver(handleScroll);

  return (
    <div className={st.Viewer}>
      <div className={st.Others}>
        <span className={st.GroupsCount}>
          {t('groups_count', { count: getGroupsCount.data?.groups_count ?? groups.length })}
        </span>
        <button
          className={st.ExpandAllButton}
          onClick={toggleExpandAll}
          type="button"
        >
          {areAllExpanded ? t('collapse_all') : t('expand_all')}
        </button>
      </div>

      <div className={st.Groups}>
        {groups.map((group) => (
          <SourceGroupCard
            group={group}
            isOpen={!collapsedGroups[group.group_id]}
            key={group.group_id}
            onToggle={() => setCollapsedGroups((prev) => ({
              ...prev,
              [group.group_id]: !prev[group.group_id],
            }))}
          />
        ))}
      </div>

      {!!getGroups.isFetchingNextPage && <Loader />}
      <div ref={sentinelRef} />
    </div>
  );
}

export { SourceGroupListViewer };
